import type { EngineId, Layer, Selection } from '@geosoft/viewer-sdk';
import { Card, Icon, Stack, Text, tokens } from '@/design';
import { LayerDetailPanel } from '@/components/layers/LayerDetailPanel';
import { ThreeDSelectionDetail } from '@/components/three-d/ThreeDSelectionDetail';
import { isThreeDLayer } from '@/components/three-d/model';

interface ShellRightPanelProps {
  engine: EngineId;
  selection: Selection | null;
  selectedLayer: Layer | null;
  mixedCrs: boolean;
}

export function ShellRightPanel({ engine, selection, selectedLayer, mixedCrs }: ShellRightPanelProps) {
  const showThreeDPanel = engine === 'cesium' || isThreeDLayer(selectedLayer);

  return (
    <aside
      role="complementary"
      aria-labelledby="right-panel-title"
      style={{
        borderLeft: tokens.border.thin,
        background: tokens.color.surface[1],
        padding: tokens.space[2],
        overflowY: 'auto',
      }}
    >
      <Stack direction="column" gap={2}>
        <Stack direction="row" align="center" gap={1}>
          <Icon name="info" size={20} decorative />
          <Text variant="subtitle" as="h2" id="right-panel-title" style={{ margin: 0 }}>
            Selection details
          </Text>
        </Stack>

        {!selection ? (
          <Card
            level={2}
            header="Nothing selected"
            data-testid="shell-right-panel-empty"
          >
            <Text variant="body" as="p" style={{ margin: 0 }}>
              Click anything on the {showThreeDPanel ? '3D scene' : 'map'} or in the layer panel to see its details.
            </Text>
          </Card>
        ) : (
          <Stack direction="column" gap={2}>
            <Card level={2} header={selection.layerId} data-testid="shell-right-panel-selection">
              <Stack direction="column" gap={1}>
                <Text variant="caption" as="span">
                  Layer: {selection.layerId}
                </Text>
                <Text variant="caption" as="span">
                  Feature: {selection.featureId ?? 'none'}
                </Text>
                <Text variant="caption" as="span">
                  Point: {selection.point ? selection.point.join(', ') : 'none'}
                </Text>
              </Stack>
            </Card>
            {showThreeDPanel ? (
              <ThreeDSelectionDetail selection={selection} selectedLayer={selectedLayer} />
            ) : (
              <LayerDetailPanel layer={selectedLayer} mixedCrs={mixedCrs} />
            )}
          </Stack>
        )}
      </Stack>
    </aside>
  );
}
